import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Spinner,
    Chip
} from "@material-tailwind/react";
import { ArrowLeftIcon, FolderIcon, CheckCircleIcon, ClockIcon } from "@heroicons/react/24/outline";
import TaskList from './TaskList';
import TaskWorkspaceShell from './TaskWorkspaceShell';
import TaskPageHeader from './TaskPageHeader';
import TaskTabsBar from './TaskTabsBar';

const WorkflowDetail = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        const fetchProject = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${rootUrl}/api/projects/${projectId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.success) {
                    setProject(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching project:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [projectId, rootUrl, token]);

    const calculateProgress = () => {
        if (!project) return 0;
        const totalTasks = project.task_count || 0;
        const completedTasks = project.completed_task_count || 0;
        if (totalTasks === 0) return 0;
        return Math.round((completedTasks / totalTasks) * 100);
    };

    const tabItems = [
        { id: 'overview', label: 'Overview', icon: FolderIcon },
        { id: 'tasks', label: 'Tasks', icon: CheckCircleIcon },
        { id: 'docs', label: 'Docs', icon: FolderIcon },
        { id: 'activity', label: 'Activity', icon: ClockIcon },
        { id: 'settings', label: 'Settings', icon: FolderIcon },
    ];

    if (loading) {
        return (
            <TaskWorkspaceShell>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <Spinner className="h-12 w-12" color="blue" />
                </div>
            </TaskWorkspaceShell>
        );
    }

    if (!project) {
        return (
            <TaskWorkspaceShell>
                <div className="min-h-[60vh] flex items-center justify-center">
                    <Typography>Workflow not found</Typography>
                </div>
            </TaskWorkspaceShell>
        );
    }

    return (
        <TaskWorkspaceShell>
            <TaskPageHeader
                title={project.name}
                subtitle={project.description || 'Workflow workspace overview and task management.'}
                icon={<FolderIcon className="h-6 w-6" />}
                actions={(
                    <Button
                        variant="text"
                        className="flex items-center gap-2"
                        onClick={() => navigate('/workflows')}
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Back
                    </Button>
                )}
                meta={project.is_archived ? (
                    <Chip value="Archived" size="sm" className="bg-gray-200 text-gray-700 w-fit" />
                ) : null}
            />

            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="shadow-sm border border-white/70">
                        <CardBody className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center text-[#FF5733]">
                                <CheckCircleIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <Typography variant="small" color="gray">Progress</Typography>
                                <Typography variant="h6" color="blue-gray">
                                    {calculateProgress()}%
                                </Typography>
                            </div>
                        </CardBody>
                    </Card>
                    <Card className="shadow-sm border border-white/70">
                        <CardBody className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center text-[#FF5733]">
                                <FolderIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <Typography variant="small" color="gray">Tasks</Typography>
                                <Typography variant="h6" color="blue-gray">
                                    {project.task_count || 0}
                                </Typography>
                            </div>
                        </CardBody>
                    </Card>
                    <Card className="shadow-sm border border-white/70">
                        <CardBody className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-2xl bg-orange-50 flex items-center justify-center text-[#FF5733]">
                                <ClockIcon className="h-6 w-6" />
                            </div>
                            <div>
                                <Typography variant="small" color="gray">Lists</Typography>
                                <Typography variant="h6" color="blue-gray">
                                    {project.list_count || 0}
                                </Typography>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <Card>
                    <CardBody>
                        <TaskTabsBar tabs={tabItems} activeTab={activeTab} onChange={setActiveTab} />
                        {activeTab === 'overview' && (
                            <Card>
                                <CardBody>
                                    <Typography color="gray">
                                        Workflow overview content will live here in the redesign.
                                    </Typography>
                                </CardBody>
                            </Card>
                        )}
                        {activeTab === 'tasks' && <TaskList projectId={projectId} embedded />}
                        {activeTab === 'docs' && (
                            <Card>
                                <CardBody>
                                    <Typography color="gray">No linked documents yet.</Typography>
                                </CardBody>
                            </Card>
                        )}
                        {activeTab === 'activity' && (
                            <Card>
                                <CardBody>
                                    <Typography color="gray">Workflow activity stream will appear here.</Typography>
                                </CardBody>
                            </Card>
                        )}
                        {activeTab === 'settings' && (
                            <Card>
                                <CardBody>
                                    <Typography color="gray">Workflow settings controls will appear here.</Typography>
                                </CardBody>
                            </Card>
                        )}
                    </CardBody>
                </Card>
            </div>
        </TaskWorkspaceShell>
    );
};

export default WorkflowDetail;
